'use client';
import {
  useAddOrUpdateCampaign,
  useAddOrUpdateCrowdFund,
} from '@/actions/feed.actions';
import getCampaignText from '@/components/Campaign/campaignTexts.constant';
import PostViewersSelectorSchema from '@/components/PostViewerSelector/postViewerSelector.validator';
import PostViewersSelectorNekiAdminSchema from '@/components/PostViewerSelector/postViewerSelectorNekiAdmin.validator';
import Button from '@/components/next-ui/Button';
import Dialog from '@/components/next-ui/Dialog';
import { CampaignType } from '@/constants/postType.constants';
import {
  useIsCommunityAdmin,
  useIsCompanyAdmin,
  useIsNonProfitAdmin,
  useIsNonProfitUser,
} from '@/hooks/useUser';
import {
  fetchUserNonProfit,
  resetNonProfitFilters,
} from '@/stores/nonProfitActions';
import { hasAppAccess } from '@/stores/userActions';
import { AppPreferenceItem } from '@/utils/appPreference';
import { useQuery } from '@tanstack/react-query';
import { Tab, TabPanel, TabPanels, Tabs, TabsList } from '@ui/Tab';
import { cx } from 'cva';
import { isEmpty, size } from 'lodash-es';
import React from 'react';
import { IoMdVideocam } from 'react-icons/io';
import { MdPhoto } from 'react-icons/md';
import * as yup from 'yup';
import AttachPostMedia from '../CreatePostDialog/AttachPostMedia';
import ImageCarousel from '../ImageCarousel';
import CreateCampaignForm from './CreateCrowdFundingForm';
import ActivitySuccessDialog from '@/components/ActivitySuccessDialog';
import useUserProfile from '@/hooks/useUserProfile';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { add } from 'date-fns';
import { PublishToPublicPage } from '@/components/PublishToPublicPage';
import { ProfileTypes, TargetViewers } from '@/constants/profileType.constant';
import { useBreakpointHooks } from '@/utils/break-points.provider';
import useStore from '@/stores/store';
import CreateCrowdFundingForm from './CreateCrowdFundingForm';

const schema = yup
  .object()
  .shape({
    title: yup
      .string()
      .trim()
      .required('Please enter a name')
      .max(110, 'Name is too long'),

    description: yup.string().trim().required('Please enter a description'),

    startDate: yup
      .date()
      .required('You must enter a start date')
      .typeError('You must enter a valid date'),

    endDate: yup
      .date()
      .required('You must enter an end date')
      .typeError('You must enter a valid date')
      .min(
        yup.ref('startDate'),
        'End date must be after or equal to start date',
      ),

    fundraisingOrgId: yup
      .number()
      .integer('fundraisingOrgId must be an integer')
      .required('fundraisingOrgId is required'),

    goalAmount: yup
      .number()
      .required('Please enter a goal amount')
      .typeError('You must enter a valid number')
      .min(1, 'Goal amount must be greater than 0'),

    contributionUrl: yup
      .string()
      .trim()
      .url('Invalid contribution URL')
      .nullable(),

    currency: yup
      .string()
      .trim()
      .matches(/^[A-Z]{3}$/, 'Currency must be a valid 3-letter ISO code')
      .nullable(),

    beneficiary: yup
      .object()
      .shape({
        beneficiaryType: yup
          .string()
          .oneOf(['individual', 'organization'], 'Invalid beneficiary type')
          .required('Beneficiary type is required'),

        beneficiaryName: yup
          .string()
          .trim()
          .required('Beneficiary name is required'),

        relationship: yup.string().trim().nullable(),
      })
      .required('Beneficiary information is required'),
  })
  .concat(PostViewersSelectorSchema);

function CreateCrowdFundingDialog(props) {
  const { open, post = null, onClose } = props;
  const isEditMode = !!post;

  const profile = useUserProfile();
  const userProfile = useStore((state) => state.user);

  const isNekiAdmin = userProfile?.isNekiAdmin;
  const isNonProfitUser = useIsNonProfitUser();
  const isNonProfitAdmin = useIsNonProfitAdmin();
  const isCommunityAdmin = useIsCompanyAdmin();
  const isAdmin = isCommunityAdmin || isNekiAdmin;

  const defaultFundraisingOrg = userProfile?.activeCommunity;
  const defaultCurrency = userProfile?.location?.currency || 'USD';

  const [reward, setReward] = React.useState(null);
  const [createdPost, setCreatedPost] = React.useState(null);
  const [submitted, setSubmitted] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [step, setStep] = React.useState('form');
  const [selectedImages, setSelectedImages] = React.useState([]);
  const [showNoImagesError, setShowNoImagesError] = React.useState(false);
  const [selectedVideos, setSelectedVideos] = React.useState([]);

  const isLessThanSmallScreen = useBreakpointHooks((bp) => bp.useSmaller('sm'));

  const getDefaultFormValues = (fundraisingOrg, crowdFundingPost) => {
    const defaultFormValues = {
      postAs: isCommunityAdmin ? 'org' : 'me',
      targetViewers: isCommunityAdmin ? 'all' : 'my-org',
      targetOrgs: [],
      isFeatured: false,
      fundraisingOrg: fundraisingOrg || null,
      title: '',
      description: '',
      goalAmount: null,
      startDate: new Date(),
      endDate: add(new Date(), { days: 90 }),
      beneficiary: {
        beneficiaryType: 'organization',
        beneficiaryName: '',
        relationShip: '',
      },
      contributionUrl: '',
      currency: defaultCurrency,
    };

    if (isEditMode && crowdFundingPost) {
      const crowdFund = crowdFundingPost.sourcePostData;
      defaultFormValues.postAs = crowdFundingPost.postAs;
      defaultFormValues.targetOrgs = crowdFundingPost.targetOrgs;
      defaultFormValues.targetViewers = crowdFundingPost.targetViewers;
      defaultFormValues.title = crowdFund.title || '';
      defaultFormValues.description = crowdFund.message || '';
      defaultFormValues.goalAmount = crowdFund?.goalAmount || null;
      defaultFormValues.startDate = new Date(crowdFund.startDate);
      defaultFormValues.endDate = new Date(crowdFund.endDate);
      defaultFormValues.videoUrl = crowdFund.videoUrl || '';
      defaultFormValues.beneficiary = crowdFund.benficiary || {
        beneficiaryType: 'organization',
        beneficiaryName: '',
        relationShip: '',
      };
      defaultFormValues.contributionUrl = crowdFund.contributionUrl || '';
      defaultFormValues.contributionUrl = crowdFund.currency || defaultCurrency;
    }

    return defaultFormValues;
  };

  const formMethods = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: getDefaultFormValues(defaultFundraisingOrg, post),
  });

  const {
    mutateAsync: addOrUpdateCrowdFund,
    isSuccess,
    isError,
  } = useAddOrUpdateCrowdFund();

  React.useEffect(() => {
    if (submitted) {
      if (isError) {
        setSubmitted(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted, isSuccess, isError]);

  const getButtonLabel = () => {
    if (isEditMode) {
      return 'Save';
    }
    return isAdmin ? 'Next' : 'Post';
  };

  const handleClose = () => {
    setStep('form');
    setSubmitted(false);
    setSelectedImages(null);
    setShowNoImagesError(false);
    setSelectedVideos(null);
    formMethods.reset(getDefaultFormValues(defaultFundraisingOrg, null));
    onClose();
  };

  const handleCreateCrowdFund = async (formValues) => {
    const payload = {
      ...formValues,
    };

    if (!isEmpty(payload.targetOrgs)) {
      payload.targetViewers = TargetViewers.SelectedOrg;
    }

    if (!submitted) {
      setSaving(true);
      setSubmitted(true);
      const data = await addOrUpdateCrowdFund({
        data: payload,
        images: selectedImages,
        videos: selectedVideos,
        post,
      });
      setStep('success');
      setReward(data?.reward);
      setCreatedPost(data?.post);
      setSaving(false);
    }
  };

  const handleNext = () => {
    if (formMethods.formState.isValid) {
      handleFormSubmit(formMethods.getValues());
    } else {
      formMethods.trigger();

      setTimeout(() => {
        if (formMethods.formState.isValid) {
          handleFormSubmit(formMethods.getValues());
        }
      }, 50);
    }
  };

  const handleFormSubmit = async (formValues) => {
    if (step === 'form') {
      if (formMethods.formState.isValid) {
        const hasImages = formValues.project
          ? true
          : !isEmpty(selectedImages) || !isEmpty(selectedVideos);
        if (hasImages) {
          if (isAdmin) {
            setStep('publish');
          } else {
            handleCreateCrowdFund(formValues);
          }
        } else {
          setShowNoImagesError(true);
        }
      }
    } else {
      // spread formValues after formData to override default values else default values are taken from formData
      const payload = {
        ...formValues,
      };

      handleCreateCrowdFund(payload);
    }
  };

  const handlePublish = async () => {
    handleCreateCrowdFund(formMethods.getValues());
  };

  const updatePostImages = (images) => {
    setSelectedImages(images || []);
    setSelectedVideos([]);
    setStep('form');
    setShowNoImagesError(false);
  };

  const updatePostVideos = (videos) => {
    setSelectedVideos(videos || []);
    setSelectedImages([]);
    setStep('form');
    setShowNoImagesError(false);
  };

  const getCampaignTitle = () => {
    return 'a Crowd Fund';
  };

  const getTitle = () => {
    const campaignTitle = getCampaignTitle();
    switch (step) {
      case 'pictures':
        return 'Add Pictures';
      case 'videos':
        return 'Add Videos';
      case 'success':
        return '';
      default:
        return isEditMode ? `Edit ${campaignTitle}` : `Create ${campaignTitle}`;
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog
      title={getTitle()}
      isOpen={open}
      maxWidth="lg"
      onClose={() => handleClose()}>
      <div className="flex w-full flex-col">
        {step === 'form' && (
          <div
            className={`flex w-full flex-col items-start justify-start overflow-y-auto pr-4`}>
            {
              <FormProvider {...formMethods}>
                <form
                  id={`create-crowd-funding-form`}
                  onSubmit={formMethods.handleSubmit((data) =>
                    handleFormSubmit(data),
                  )}
                  className="h-full w-full">
                  <CreateCrowdFundingForm
                    isEditMode={isEditMode}
                    images={selectedImages}
                    videos={selectedVideos}
                    isNekiAdmin={isNekiAdmin}
                  />
                </form>
              </FormProvider>
            }

            {size(selectedImages) > 0 && (
              <div className="mt-6 flex w-full items-center justify-start gap-4">
                <ImageCarousel
                  size="xs"
                  images={selectedImages}
                  onChange={updatePostImages}
                  disabled={isEditMode}
                />
              </div>
            )}
            {!isEmpty(selectedVideos) && (
              <div className="mt-6 flex w-full items-center justify-start gap-4">
                <ImageCarousel
                  type="video"
                  images={selectedVideos}
                  onChange={updatePostVideos}
                  disabled={isEditMode}
                />
              </div>
            )}
          </div>
        )}

        {step === 'pictures' && (
          <AttachPostMedia
            type="image"
            onChange={updatePostImages}
            assets={selectedImages || []}
          />
        )}

        {step === 'videos' && (
          <AttachPostMedia
            type="video"
            onChange={updatePostVideos}
            assets={selectedVideos || []}
          />
        )}

        {step === 'form' && (
          <div
            className={`flex w-full items-center justify-start gap-4 pt-4 ${
              isLessThanSmallScreen ? 'mb-32' : ''
            }`}>
            {!isEditMode && (
              <>
                <Button
                  variant="icon"
                  className="bg-body-background"
                  onClick={() => setStep('pictures')}>
                  <MdPhoto
                    className={cx(
                      'h-6 w-6 ',
                      showNoImagesError
                        ? 'animate-pulse text-error'
                        : 'text-text-disabled',
                    )}
                  />
                </Button>

                <Button
                  variant="icon"
                  className="bg-body-background"
                  onClick={() => setStep('videos')}>
                  <IoMdVideocam className="h-6 w-6 text-text-disabled" />
                </Button>
              </>
            )}

            <div className="ml-auto">
              <Button
                color="primary"
                variant="filled"
                size="md"
                loading={saving}
                onClick={handleNext}>
                {getButtonLabel()}
              </Button>
            </div>
          </div>
        )}

        {step === 'publish' && (
          <div
            className={`flex flex-1 flex-col gap-4 ${
              isLessThanSmallScreen ? 'mb-32' : ''
            }`}>
            <div className="flex-1">
              <FormProvider {...formMethods}>
                <PublishToPublicPage
                  isAdmin={isAdmin}
                  isNekiAdmin={isNekiAdmin}
                />
              </FormProvider>
            </div>

            <div className="ml-auto">
              <Button
                color="primary"
                variant="filled"
                size="md"
                loading={saving}
                onClick={handlePublish}>
                Post
              </Button>
            </div>
          </div>
        )}
        {step === 'success' && (
          <ActivitySuccessDialog
            isPost
            user={profile}
            reward={reward}
            post={createdPost}
            onClose={handleClose}
            isPostPublic={createdPost?.targetViewers === TargetViewers.All}
          />
        )}
      </div>
    </Dialog>
  );
}

export default CreateCrowdFundingDialog;
